// BusinessPage.tsx — Section IV: Business.
//
// "Subscriptions, revenue, refunds, disputes. The ledger of paying members."
// (Lore-approved BZ-03)
//
// Wire: subscription_canister.admin_get_metrics() via admin_get_aggregated_metrics().
// Empty state when no subscriptions yet.
// Note: failedPaymentsAwaitingRetry / refundsLast30d / disputesOpen return 0
//       sentinels per K2 lock — shown with caption "see Stripe dashboard for live".

import React from 'react';
import type { Identity } from '@dfinity/agent';
import MetricCard from '../components/MetricCard';
import { useAdminMetrics } from '../hooks/useAdminMetrics';

interface BusinessPageProps {
  identity: Identity | null;
}

function formatCents(cents: bigint): string {
  const total = Number(cents);
  const dollars = Math.floor(total / 100);
  const sub = String(total % 100).padStart(2, '0');
  return '$' + dollars.toLocaleString('en-US') + '.' + sub;
}

function formatNat(n: bigint): string {
  return Number(n).toLocaleString('en-US');
}

export default function BusinessPage({ identity }: BusinessPageProps): React.ReactElement {
  const { metrics, isLoading, lastFetchedAt, refresh } = useAdminMetrics(identity);

  const sub = metrics?.subscription?.[0] ?? null;

  const hasData = sub !== null;

  return (
    <section className="section" id="business" aria-labelledby="business-title">
      <header className="section-header">
        <span className="section-eyebrow">Section IV</span>
        <h2 className="section-title" id="business-title">Business</h2>
        <p className="section-lede">
          Subscriptions, revenue, refunds, disputes. The ledger of paying members.
        </p>
        {lastFetchedAt && (
          <p className="section-meta">
            Last refreshed · {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}
            <button className="btn-inline" onClick={refresh} disabled={isLoading}>
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </p>
        )}
      </header>

      {!hasData && !isLoading ? (
        <div className="empty-state">
          <p className="empty-state-text">
            No subscriptions yet. The first will arrive after launch.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-4">
            <MetricCard
              label="Active Subscriptions"
              numeral={sub ? formatNat(sub.activeSubscriptions) : '—'}
              caption="Renewing on cycle."
            />
            <MetricCard
              label="In Trial"
              numeral={sub ? formatNat(sub.activeTrials) : '—'}
              caption="Card on file. Yet to be charged."
              trend={sub ? `+${formatNat(sub.trialsStartedLast7d)} / 7d` : undefined}
              trendDir="up"
            />
            <MetricCard
              label="MRR"
              numeral={sub ? formatCents(sub.mrrCents) : '—'}
              caption="Net of refunds."
            />
            <MetricCard
              label="Lifetime Revenue"
              numeral="—"
              caption="Gross. Stripe ledger."
            />
            <MetricCard
              label="Signups / 7d"
              numeral={sub ? formatNat(sub.signupsLast7d) : '—'}
              caption="New paid conversions."
            />
            <MetricCard
              label="Churn / 30d"
              numeral={sub ? formatNat(sub.churnLast30d) : '—'}
              caption="Cancellations and lapses."
            />
            <MetricCard
              label="Refunds / 30d"
              numeral={sub ? formatNat(sub.refundsLast30d) : '0'}
              caption={`${sub ? formatCents(sub.refundsLast30d * 499n) : '$0.00'} returned. See Stripe dashboard for live.`}
            />
            <MetricCard
              label="Disputes Open"
              numeral={sub ? formatNat(sub.disputesOpen) : '0'}
              caption="Stripe Radar watch. See Stripe dashboard for live."
            />
          </div>
        </>
      )}
    </section>
  );
}
